import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editHigherAdminFeedbackRequest } from "services/appealService";
import { HigherAdminFeedbackRequest } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  feedbackId: number;
  feedback: HigherAdminFeedbackRequest;
  onClose: (refetch: boolean) => void;
};

const RegistrarHigherAdminFeedback: FC<Props> = ({
  feedbackId,
  feedback,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);

  type FormState = {
    higher_admin_feedback: string | null;
    higher_admin_feedback_date: string | null;
    feedback_files: File | null;
  };

  const [formState, setFormState] = useState<FormState>({
    higher_admin_feedback: null,
    higher_admin_feedback_date: null,
    feedback_files: null,
  });

  useEffect(() => {
    if (feedback) {
      setFormState({
        higher_admin_feedback: feedback?.higher_admin_feedback ?? null,
        higher_admin_feedback_date: feedback?.higher_admin_feedback_date
          ? new Date(feedback?.higher_admin_feedback_date)
              .toISOString()
              .split("T")[0]
          : null,
        feedback_files: null,
      });
    }
  }, [feedback]);

  const handleChange = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formState?.higher_admin_feedback)
      return t("registration_number_is_required");
    else if (!formState?.higher_admin_feedback_date)
      return t("higher_admin_feedback_date_is_required");
    else if (!formState?.feedback_files)
      return t("higher_admin_feedback_file_is_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    editHigherAdminFeedbackRequest({
      ...formState,
      id: feedbackId,
      status: "FORWARDED_TO_HIGHER_COORDINATOR",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("higher_admin_feedback")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("higher_admin_feedback_detail")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("higher_admin_feedback_label")}
            </label>
            <textarea
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="higher_admin_feedback"
              rows={4}
              value={formState?.higher_admin_feedback ?? ""}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("higher_admin_feedback_date_label")}
            </label>
            <input
              type="date"
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="higher_admin_feedback_date"
              value={
                formState?.higher_admin_feedback_date
                  ? new Date(formState.higher_admin_feedback_date)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={handleChange}
            />
          </div>
          <div className="mt-4 flex flex-row gap">
            <FileUpload
              label={t("higher_admin_feedback_file_label")}
              fileTypes="*"
              required={true}
              onChange={(file) =>
                setFormState((prev) => ({
                  ...prev,
                  feedback_files: file,
                }))
              }
            />
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                t("submit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrarHigherAdminFeedback;
